

import { Document, Page, View, Image, Text, StyleSheet } from '@react-pdf/renderer';
import { LaudoData } from '../types';
import {
  Identificacao,
  RelatorioFotografico,
} from './sections';
import { ChecklistBody } from './sections/Checklist';
import { NormalCalculosBody } from './sections/NormalCalculos';
import {
  AVMPage1Identificacao,
  AVMPage2Amostras,
  AVMPage3Calculos,
} from './sections/AVM';
import { Page2AmostrasBody } from './sections/AVM/Page2Amostras';
import { AVMPageFooter } from './sections/AVM/components';

const combinedStyles = StyleSheet.create({
  page: { width: 595, height: 842, backgroundColor: '#FFFFFF', flexDirection: 'column', fontFamily: 'Helvetica', paddingBottom: 40 },
  headerBar: { paddingBottom: 8, borderBottom: '1 solid #D6DBE7', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
});

interface LaudoDocumentProps {
  data: LaudoData;
  logoBase64?: string | undefined;
  isAutomatic?: boolean;
  streetViewImageBase64?: string;
}

export function LaudoDocument({ data, logoBase64, isAutomatic = false, streetViewImageBase64 }: LaudoDocumentProps) {
  return (
    <Document
      title={`Laudo de Avaliação ${data.header.idLaudo}`}
      author={data.responsavelLaudo.nomeResponsavel}
      subject="Laudo de Avaliação de Imóvel"
      creator="Banco Bari - Sistema de Inspeções"
      producer="@react-pdf/renderer"
    >
      {isAutomatic ? (
        /* ══════════════════════════════════════════════════════════════
         *  LAYOUT AUTOMÁTICO (AVM) — Design Bari branded
         * ══════════════════════════════════════════════════════════════ */
        <>
          {/* Page 1: Dados do Pedido + Dados do Imóvel + Resumo da Avaliação */}
          <AVMPage1Identificacao
            header={data.header}
            dadosPedido={data.dadosPedido}
            dadosImovel={data.dadosImovel}
            resumoAvaliacao={data.resumoAvaliacao}
            streetViewImageBase64={streetViewImageBase64}
            footer={data.footer}
            logoBase64={logoBase64}
          />

          {/* Page 2: Amostras Comparáveis */}
          <AVMPage2Amostras
            header={data.header}
            amostras={data.amostras}
            footer={data.footer}
            logoBase64={logoBase64}
          />

          {/* Page 3: Avaliação do Imóvel + Tabela de Cálculos + Homogeneização + Detalhamento + Valores + Responsável */}
          <AVMPage3Calculos
            header={data.header}
            tabelaCalculo={data.calculosAvaliatorios.tabelaCalculo}
            legendaFatores={data.calculosAvaliatorios.legendaFatores}
            fatoresAtivos={data.calculosAvaliatorios.fatoresAtivos}
            mediaValorUnitarioOriginal={data.calculosAvaliatorios.mediaValorUnitarioOriginal}
            mediaValorHomogeneizado={data.calculosAvaliatorios.mediaValorHomogeneizado}
            homogeneizacao={data.homogeneizacao}
            detalhamentoValores={data.detalhamentoValores}
            avaliacaoFinal={data.avaliacaoFinal}
            responsavelLaudo={data.responsavelLaudo}
            footer={data.footer}
            logoBase64={logoBase64}
            metodologia={data.avaliacaoMetodologia.metodologia}
            tipoImovel={data.resumoAvaliacao.tipoImovel}
            metodologiaAplicada={data.resumoAvaliacao.metodologiaAplicada}
            grauPrecisao={data.resumoAvaliacao.grauPrecisao}
            grauFundamentacao={data.resumoAvaliacao.grauFundamentacao}
            comentarios={data.comentarios}
          />
        </>
      ) : (
        /* ══════════════════════════════════════════════════════════════
         *  LAYOUT NORMAL (Vistoria) — Layout original
         * ══════════════════════════════════════════════════════════════ */
        <>
          {/* Page 1: Identificação + Resumo da Avaliação */}
          <Identificacao
            header={data.header}
            dadosPedido={data.dadosPedido}
            dadosImovel={data.dadosImovel}
            resumoAvaliacao={data.resumoAvaliacao}
            detalhamentoValores={data.detalhamentoValores}
            fotoFachadaUrl={data.fotoFachadaUrl}
            footer={data.footer}
            logoBase64={logoBase64}
          />

          {/* Pagina única combinada: Questionário + Amostras + Cálculos (fluem livremente, sem gap entre seções) */}
          <Page size="A4" style={combinedStyles.page}>
            {/* Fixed header que repete em todas as páginas */}
            <View fixed style={{ paddingHorizontal: 16, paddingTop: 16 }}>
              <View style={combinedStyles.headerBar}>
                {logoBase64 ? (
                  <Image src={logoBase64} style={{ width: 64, height: 24, objectFit: 'contain' }} />
                ) : (
                  <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#101010' }}>bari.</Text>
                )}
                <View style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <Text style={{ textAlign: 'right', color: '#000000', fontSize: 10, fontWeight: 'bold' }}>LAUDO DE AVALIAÇÃO</Text>
                  <Text style={{ textAlign: 'right', color: '#000000', fontSize: 9 }}>{data.header.idLaudo}  ·  {data.header.formatoLaudo}</Text>
                </View>
              </View>
            </View>

            {/* Conteúdo combinado — flui e quebra páginas automaticamente */}
            <View style={{ paddingHorizontal: 16, paddingTop: 16, flexDirection: 'column', gap: 16 }}>
              <ChecklistBody vistoriaImovel={data.vistoriaImovel} />
              <Page2AmostrasBody amostras={data.amostras} />
              <NormalCalculosBody
                tabelaCalculo={data.calculosAvaliatorios.tabelaCalculo}
                legendaFatores={data.calculosAvaliatorios.legendaFatores}
                fatoresAtivos={data.calculosAvaliatorios.fatoresAtivos}
                mediaValorUnitarioOriginal={data.calculosAvaliatorios.mediaValorUnitarioOriginal}
                mediaValorHomogeneizado={data.calculosAvaliatorios.mediaValorHomogeneizado}
                tabelaDepreciacao={data.tabelaDepreciacao}
                tabelaAreaConstruida={data.tabelaAreaConstruida}
                tabelaValorTerreno={data.tabelaValorTerreno}
                homogeneizacao={data.homogeneizacao}
                detalhamentoValores={data.detalhamentoValores}
                avaliacaoFinal={data.avaliacaoFinal}
                responsavelLaudo={data.responsavelLaudo}
                comentarios={data.comentarios}
                metodologia={data.avaliacaoMetodologia.metodologia}
                tipoImovel={data.resumoAvaliacao.tipoImovel}
                metodologiaAplicada={data.resumoAvaliacao.metodologiaAplicada}
                grauPrecisao={data.resumoAvaliacao.grauPrecisao}
                grauFundamentacao={data.resumoAvaliacao.grauFundamentacao}
              />
            </View>

            <AVMPageFooter footer={data.footer} />
          </Page>

          {/* Relatório Fotográfico */}
          <RelatorioFotografico
            header={data.header}
            questionario={data.vistoriaImovel.questionario}
            footer={data.footer}
            logoBase64={logoBase64}
            photoOrder={data.photoOrder}
          />
        </>
      )}
    </Document>
  );
}
